import { useRef } from "react";
import ImagePlaceholderIcon from "@/components/SVGComponents/ImagePlaceholderIcon";
import Button from "@/components/Button/Button";
import ErrorText from "@/components/ErrorText/ErrorText";
import { combineClasses } from "@/lib/utils";
import styles from "./CoverImageUpload.module.scss";

export interface CoverImageUploadProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
  error?: string | undefined;
  existingImageUrl?: string | null | undefined;
}

// The picked `File` is now lifted to CreateGathering.tsx — it's sent in
// the same multipart create/edit request as the rest of the payload (see
// `buildEventFormData` in services/api/events.ts), not as part of this
// component's own local state.
const CoverImageUpload = ({
  file,
  onFileChange,
  disabled = false,
  error,
  existingImageUrl,
}: CoverImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const captionText =
    file?.name ||
    (existingImageUrl ? "Existing cover image attached" : "1200 × 675 · JPG or PNG · shown behind the gathering header");

  return (
    <div className={styles.container}>
      <div className={combineClasses( file || existingImageUrl ? styles.wrapperimageUploaded : styles.wrapper , Boolean(error) && styles.wrapperError)}>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png"
          disabled={disabled}
          className={styles.hiddenInput}
          onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        />
        <ImagePlaceholderIcon className={styles.icon} />
        <div className={styles.text}>
          <span className={styles.title}>
            Cover image <span className={styles.requiredStar}>*</span>
          </span>
          <span className={styles.caption}>{captionText}</span>
        </div>
        <Button outlined disabled={disabled} className={styles.uploadButton} onClick={() => inputRef.current?.click()}>
          {file || existingImageUrl ? "CHANGE" : "UPLOAD"}
        </Button>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
};

export default CoverImageUpload;
